import React, { useState, useEffect, useCallback } from "react";
import { createRechargeTracking, findUser, updateUser } from "../api/userApi";
import { getOffers } from "../api/offerApi";
import SerialPortComponent from "../components/SerialPortComponent";
import { Bounce, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { 
  Search, User, CreditCard, Coins, Package, DollarSign, 
  PlusCircle, CheckCircle, Loader2, ReceiptText, RefreshCw 
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function TokenRecharge() {
      const { userData } = useAuth();
  const [user, setUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [offers, setOffers] = useState([{id:1,name:"Silver",tokens:1500,price:34,discount:10},{id:2,name:"GOLD",tokens:1800,price:52,discount:10},{id:3,name:"Premium",tokens:2200,price:55,discount:10}]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const { t } = useTranslation();

  // Rate to convert tokens to USD (example: 10 tokens = $1)
  const TOKEN_TO_USD_RATE = 0.1;

  // Fetch offers
  useEffect(() => {
    const fetchOffers = async () => {
      setIsLoadingOffers(true);
      try {
        const data = await getOffers();
        setOffers(data);
      } catch (error) {
        console.error("Error fetching offers:", error);
        toast.error(t('errorFetchingOffers'), { transition: Bounce });
      } finally {
        setIsLoadingOffers(false);
      }
    };

    fetchOffers();
  }, [t]);

  // Search user by email, mobile, or user_name
  const searchUser = useCallback(async () => {
    if (!searchText.trim()) return;
    
    setIsSearching(true);
    try {
      const foundUser = await findUser(searchText);
      if (foundUser) {
        setUser(foundUser);
        toast.success(t('userFound'), { transition: Bounce });
      } else {
        setUser(null);
        toast.error(t('userNotFound'), { transition: Bounce });
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error(t('searchError'), { transition: Bounce });
    } finally {
      setIsSearching(false);
    }
  }, [searchText, t]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      searchUser();
    }
  };

  const handleCardData = useCallback(async (cardData) => {
    if (!cardData) return;
    console.log("Received card data:", cardData);
    
    if (cardData.part4 === "1") {
      try {
        setIsSearching(true);
        // Search user by card ID
        const foundUser = await findUser(cardData.part1);
        if (foundUser) {
          setUser(foundUser);
          toast.success(t('userFoundByCard'), { transition: Bounce });
        }
      } catch (error) {
        console.error("Error finding user by card:", error);
        toast.error(t('errorFindingUserByCard'), { transition: Bounce });
      } finally {
        setIsSearching(false);
      }
    } 

    if(cardData.part4 === "0"){ setUser(null);
        toast.success(t('Card Removed'), { transition: Bounce });}
  }, [t]);

  const handleRecharge = async () => {
    if (!user) return;
    
    let tokenAmount = 0;
    
    if (selectedOffer) {
      tokenAmount = selectedOffer.tokens;
    } else if (showCustomAmount && customAmount) {
      tokenAmount = parseInt(customAmount, 10);
      if (isNaN(tokenAmount) || tokenAmount <= 0) {
        toast.error(t('invalidTokenAmount'), { transition: Bounce });
        return;
      }
    } else {
      toast.error(t('selectOfferOrAmount'), { transition: Bounce });
      return;
    }
    
    setIsProcessing(true);
    try {
      const currentTokens = user.vigor_coins || 0;
      const newTokens = currentTokens + tokenAmount;
      const payload = 
      {user_id: user.id, mediator_id: userData.id,vigor_coins:newTokens, tokens: tokenAmount, paidAmount:calculateCost() }
      const updatedUser = await createRechargeTracking(payload);
      setUser(updatedUser.user);
      
      toast.success(
        t('tokensAddedSuccess', { amount: tokenAmount, total: newTokens }), 
        { transition: Bounce }
      );
      
      // Reset selections
      setSelectedOffer(null);
      setCustomAmount("");
      setShowCustomAmount(false);
    } catch (error) {
      console.error("Error recharging tokens:", error);
      toast.error(t('errorRecharging'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const selectOffer = (offer) => {
    setSelectedOffer(offer);
    setShowCustomAmount(false);
  };

  const startCustomAmount = () => {
    setSelectedOffer(null);
    setShowCustomAmount(true);
    setCustomAmount("");
  };

  const calculateCost = () => {
    if (selectedOffer) {
      return selectedOffer.price;
    } else if (showCustomAmount && customAmount) {
      const amount = parseInt(customAmount, 10);
      return isNaN(amount) ? 0 : (amount * TOKEN_TO_USD_RATE).toFixed(2);
    }
    return 0;
  };

  const refreshData = async () => {
    if (!user) return;
    
    setIsSearching(true);
    try {
      const refreshedUser = await findUser(user.user_name || user.user_email);
      if (refreshedUser) {
        setUser(refreshedUser);
        toast.info(t('dataRefreshed'), { transition: Bounce });
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error(t('errorRefreshing'), { transition: Bounce });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mb-9">
            <div className="mb-6 flex items-center justify-between">
      <h1 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
        <Coins size={24} className="mr-2" />
        {t('tokenRechargeSystem')}
      </h1>
     
        <SerialPortComponent onCardData={handleCardData} />
      </div>
      
      {/* Search Section */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Search size={20} className="mr-2" /> {t('findUser')}
        </h2>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('searchPlaceholder')}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
            />
            <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
          </div>
          <button
            onClick={searchUser}
            disabled={!searchText.trim() || isSearching}
            className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2"
          >
            {isSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
            {t('search')}
          </button>
        </div>
        
        <p className="text-sm text-gray-500 mt-2">
          {t('searchOrSwipeCard')}
        </p>
      </div>
      
      {/* User Profile & Recharge Section */}
      {user && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* User Info */}
          <div className="p-6 border-b">
            <div className="flex justify-between items-start">
              <h2 className="text-xl font-semibold flex items-center">
                <User size={20} className="mr-2" /> {t('userProfile')}
              </h2>
              <button 
                onClick={refreshData}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
              >
                <RefreshCw size={14} /> {t('refresh')}
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-3">
                <div>
                  <span className="text-gray-500 text-sm">{t('fullName')}</span>
                  <p className="font-medium">{user.full_name}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">{t('username')}</span>
                  <p className="font-medium">{user.user_name}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-gray-500 text-sm">{t('email')}</span>
                  <p className="font-medium">{user.user_email}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">{t('membershipCard')}</span>
                  <p className="font-medium">
                    {user.user_card ? 
                      <span className="flex items-center gap-1">
                        <CreditCard size={16} className="text-green-600" />
                        {user.user_card}
                      </span> : 
                      <span className="text-gray-400">{t('noCardAssigned')}</span>
                    }
                  </p>
                </div>
              </div>
            </div>
            
            {/* Current Balance */}
            <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Coins size={24} className="text-blue-600 mr-2" />
                  <div>
                    <h3 className="font-semibold text-gray-800">{t('currentBalance')}</h3>
                    <p className="text-sm text-gray-600">{t('availableTokens')}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-blue-700">
                    {user.vigor_coins || 0}
                  </span>
                  <p className="text-sm text-gray-500">{t('tokens')}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Recharge Options */}
          <div className="p-6 bg-gray-50">
            <h3 className="font-semibold text-lg mb-4 flex items-center">
              <PlusCircle size={18} className="mr-2" /> {t('rechargeOptions')}
            </h3>
            
            {/* Predefined Offers */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                <Package size={16} className="mr-2" /> {t('specialOffers')}
              </h4>
              
              {isLoadingOffers ? (
                <div className="flex justify-center p-4">
                  <Loader2 size={24} className="animate-spin text-blue-600" />
                </div>
              ) : offers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {offers.map((offer) => (
                    <button
                      key={offer.id}
                      onClick={() => selectOffer(offer)}
                      className={`p-3 rounded-lg border transition-all ${
                        selectedOffer?.id === offer.id
                          ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                          : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-gray-800">{offer.name}</div>
                        {selectedOffer?.id === offer.id && (
                          <CheckCircle size={18} className="text-green-500" />
                        )}
                      </div>
                      <div className="flex justify-between items-end">
                        <div className="flex items-center text-blue-700">
                          <Coins size={16} className="mr-1" />
                          <span className="font-bold">{offer.tokens}</span>
                        </div>
                        <div className="text-green-700 font-medium flex items-center">
                          <DollarSign size={14} />
                          {offer.price}
                        </div>
                      </div>
                      {offer.discount > 0 && (
                        <div className="mt-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full inline-block">
                          {offer.discount}% {t('off')}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">{t('noOffersAvailable')}</p>
              )}
              
              {/* Custom Amount Option */}
              <div className="mt-4">
                <button
                  onClick={startCustomAmount}
                  className={`p-3 rounded-lg border w-full sm:w-auto transition-all ${
                    showCustomAmount
                      ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                      : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-800">{t('customAmount')}</span>
                    {showCustomAmount && <CheckCircle size={18} className="text-green-500 ml-2" />}
                  </div>
                </button>
                
                {showCustomAmount && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="number"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder={t('enterAmount')}
                        min="1"
                        className="px-4 py-2 border rounded-md w-32 pl-9"
                      />
                      <Coins size={16} className="absolute left-3 top-2.5 text-gray-400" />
                    </div>
                    <div className="text-gray-600">
                      = <DollarSign className="inline-block mx-1" size={14} />
                      <span className="font-medium">
                        {
                          customAmount && !isNaN(parseInt(customAmount, 10))
                            ? (parseInt(customAmount, 10) * TOKEN_TO_USD_RATE).toFixed(2)
                            : '0.00'
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Summary & Confirm */}
            <div className="mt-6 border-t pt-4">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium">{t('summary')}</h4>
                  <div className="text-sm text-gray-600 mt-1">
                    {selectedOffer ? (
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {selectedOffer.name}: {selectedOffer.tokens} {t('tokens')}
                      </span>
                    ) : showCustomAmount && customAmount ? (
                      <span className="flex items-center gap-1">
                        <Coins size={14} />
                        {customAmount} {t('tokens')}
                      </span>
                    ) : (
                      <span className="text-gray-400 italic">{t('selectOption')}</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-gray-500 text-sm">{t('totalCost')}</div>
                  <div className="font-bold text-xl flex items-center justify-end">
                    <DollarSign size={16} />
                    {calculateCost()}
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleRecharge}
                disabled={(!selectedOffer && (!showCustomAmount || !customAmount)) || isProcessing}
                className="mt-4 bg-green-600 text-white w-full py-3 rounded-lg hover:bg-green-700 disabled:bg-green-300 flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <ReceiptText size={20} />
                )}
                {t('confirmRecharge')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {!user && !isSearching && (
        <div className="bg-white p-8 rounded-lg shadow-md text-center border-2 border-dashed border-gray-200">
          <User size={48} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">{t('noUserSelected')}</h3>
          <p className="text-gray-500 mb-4">
            {t('searchUserOrSwipeCardPrompt')}
          </p>
        </div>
      )}
    </div>
  );
}